import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, testimonials } from '@/mocks/about';

export default function AboutPage() {
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    description: aboutInfo.introduction,
    url: 'https://readdy.ai',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
    },
  };

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们',
    description: aboutInfo.introduction,
    url: 'https://readdy.ai/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center mb-16">
                <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-6">
                  关于我们
                </h1>
                <p className="text-lg text-primary-600 font-heading font-medium mb-6">
                  {aboutInfo.tagline}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
                {aboutInfo.stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-background-100 rounded-xl">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="mb-20">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 text-center mb-10">
                  我们的价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {aboutInfo.values.map((val, index) => (
                    <div
                      key={index}
                      className="p-6 bg-background-100 rounded-xl border border-background-200/70 text-center"
                    >
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                        {val.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-20">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 text-center mb-10">
                  专家团队
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {aboutInfo.team.map((member, index) => (
                    <div
                      key={index}
                      className="p-6 bg-background-50 rounded-xl border border-background-200/70 text-center"
                    >
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className="ri-user-3-line text-2xl text-accent-600"></i>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                        {member.name}
                      </h3>
                      <p className="text-xs text-primary-600 font-medium mb-3">{member.role}</p>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {member.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 text-center mb-10">
                读者口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {testimonials.map((item, index) => (
                  <div
                    key={index}
                    className="p-6 bg-background-50 rounded-xl border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-4 text-accent-500">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{item.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                      <span className="text-xs text-foreground-400">{item.age}岁</span>
                      <span className="text-xs text-foreground-300">· {item.location}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mb-10 max-w-lg mx-auto">
                如果你有任何关于女性健康的疑问，或者想加入我们的科普团队，欢迎通过以下方式联系我们。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{aboutInfo.contact.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-lg text-accent-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{aboutInfo.contact.wechat}</span>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}